import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SummaryApi from '../common'
import { setUserDetails } from '../store/userSlice'

// Silent-refresh hook. Mounted once at the top of the React tree (App.js).
//
// How it works:
//   1. Every successful auth response sets a non-httpOnly companion cookie
//      `access_token_expires_at` = ISO timestamp.
//   2. This hook reads that cookie, computes how long until expiry, schedules a
//      single timer to fire 60 seconds BEFORE expiry, and POSTs /auth/refresh.
//   3. The refresh endpoint rotates both cookies; the new `access_token_expires_at`
//      gets read on the next render, and the timer reschedules.
//   4. If the refresh fails (reuse-detected, invalid, network), we clear the user
//      state and let `<ProtectedRoute>` bounce to /login.
//
// Edge cases handled:
//   - No logged-in user → no timer (we only refresh authed sessions).
//   - Expiry already past → fire refresh immediately rather than scheduling negative ms.
//   - Multiple tabs → each tab independently schedules a refresh, but `validateAndRotate`
//     is reuse-aware: the slower tab will hit a reuse_detected and get logged out. To
//     improve this we could coordinate via a BroadcastChannel, but the current behavior
//     is correct (the user can re-log in cleanly).
//
// Safety margins:
//   - Refresh fires 60s early so the user never makes a request with an expired token.
//   - Polling interval (when no expiry cookie) is 5 minutes — handles the rare race
//     where the cookie was set but we missed the initial read.

const REFRESH_LEAD_MS = 60 * 1000    // refresh 60s before expiry
const POLL_INTERVAL_MS = 5 * 60_000  // fallback poll if no expiry cookie is readable
const MIN_DELAY_MS = 1000            // never schedule more than once a second

function readExpiryCookie() {
    if (typeof document === 'undefined') return null
    const m = document.cookie.match(/(?:^|;\s*)access_token_expires_at=([^;]+)/)
    if (!m) return null
    const raw = decodeURIComponent(m[1])
    const t = Date.parse(raw)
    return Number.isFinite(t) ? t : null
}

async function refreshNow(dispatch) {
    try {
        const res = await fetch(SummaryApi.refresh_token.url, {
            method: SummaryApi.refresh_token.method,
            credentials: 'include',
        })
        if (!res.ok) {
            // Includes reuse_detected, expired, invalid — all terminal. Drop the user
            // from Redux; the next protected-route render bounces to /login.
            dispatch(setUserDetails(null))
            return false
        }
        const json = await res.json().catch(() => null)
        if (json?.success && json?.data) {
            dispatch(setUserDetails(json.data))
        }
        return true
    } catch {
        // Network failure isn't terminal — leave the user alone and the next scheduled
        // attempt will try again. If the access token expires in the meantime, normal
        // 401 handling kicks in and the Axios/fetch interceptors clear the session.
        return false
    }
}

export default function useAccessTokenAutoRefresh() {
    const dispatch = useDispatch()
    const authedUserId = useSelector(s => s?.user?.user?._id)
    const timerRef = useRef(null)

    useEffect(() => {
        // Clear any prior timer when the auth status flips, deps change, or we unmount.
        function clearTimer() {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }

        if (!authedUserId) {
            clearTimer()
            return
        }

        function schedule() {
            clearTimer()
            const expiresAtMs = readExpiryCookie()
            // No cookie yet (first-render race) → check again on the polling interval.
            if (!expiresAtMs) {
                timerRef.current = setTimeout(schedule, POLL_INTERVAL_MS)
                return
            }
            const fireAt = expiresAtMs - REFRESH_LEAD_MS
            const delay = Math.max(MIN_DELAY_MS, fireAt - Date.now())
            timerRef.current = setTimeout(async () => {
                const ok = await refreshNow(dispatch)
                if (ok) schedule() // next cycle
                // If !ok, user was cleared → effect re-runs with authedUserId=null → timer stays cleared.
            }, delay)
        }

        schedule()

        return clearTimer
    }, [authedUserId, dispatch])
}
